import { loginRequest, getProfile } from "../services/authService";
import { setMultimonedaFlag } from "../../../shared/hooks/useMultimoneda";
import { setTenantTipo } from "../../../shared/hooks/useTenantTipo";
import apiFetch from "../../../shared/services/apiClient";
import { limpiarSesion } from "../../../shared/utils/sesion";

export function useAuth() {
  async function login(username, password) {
    const data = await loginRequest(username, password);

    // El "visto" del changelog no es dato de sesión — es una preferencia del
    // navegador ("ya vi la v1.5.0"). Sin preservarlo, el clear() de abajo lo
    // borra y el modal de Novedades reaparece en cada login en vez de una
    // sola vez por versión.
    // Wipe every key from any previous session before writing new user's data.
    // This prevents role/condominio data from a previous user leaking into the
    // new session when two different accounts are used in the same browser.
    // `limpiarSesion` conserva las preferencias del dispositivo (idioma y la
    // marca de novedades vistas); el resto se borra.
    limpiarSesion();

    localStorage.setItem("access_token", data.access);
    localStorage.setItem("refresh_token", data.refresh);
    localStorage.setItem("login_at", String(Date.now()));

    const perfil = await getProfile();
    const uc  = perfil?.usuario_condominio;
    const rol = uc?.rol || "USUARIO";

    localStorage.setItem("is_superuser", perfil?.is_superuser ? "true" : "false");
    localStorage.setItem("rol", rol);
    // Qué módulos puede ver (usuario, contador u operador de agua). El
    // superadministrador lo configura en el panel admin; ver shared/utils/permisos.js.
    localStorage.setItem("permisos", JSON.stringify(uc?.permisos || []));

    if (rol === "CONTADOR" || rol === "AGUA" || rol === "SEGURIDAD") {
      // El operador de agua trabaja de día, en el campo y desde el celular:
      // arranca en tema claro y con la marca azul del módulo. El contador
      // mantiene el tema oscuro y el naranja de siempre.
      localStorage.setItem("theme", rol === "AGUA" || rol === "SEGURIDAD" ? "light" : "dark");
      if (rol === "AGUA") localStorage.setItem("brand", "agua");
      // El guarda arranca en tema claro y lo cambia desde la Garita si quiere.
      // Los tres roles son multi-condominio y comparten la misma tabla de accesos.
      const condominios = uc?.condominios_contador || [];
      localStorage.setItem("condominios_contador", JSON.stringify(condominios));
      // Restore last selected condominio or default to first
      const savedId  = localStorage.getItem("active_condominio_id");
      const validIds = condominios.map(c => String(c.id));
      let activeId   = savedId && validIds.includes(savedId) ? savedId : String(condominios[0]?.id ?? "");
      if (!savedId || !validIds.includes(savedId)) {
        if (condominios.length > 0) {
          localStorage.setItem("active_condominio_id", activeId);
        }
      }
      // Sync multimoneda del condominio activo
      const activeCondo = condominios.find(c => String(c.id) === String(activeId));
      setMultimonedaFlag(!!activeCondo?.multimoneda_activa);
      setTenantTipo(activeCondo?.tipo || "condominio");
    } else if (rol === "RESIDENTE") {
      localStorage.setItem("theme", "light");
      if (uc?.filial_id) localStorage.setItem("filial_id", String(uc.filial_id));
      if (uc?.filial_nombre) localStorage.setItem("filial_nombre", uc.filial_nombre);
      if (uc?.condominio_nombre) localStorage.setItem("condominio_nombre", uc.condominio_nombre);
      if (perfil?.first_name) localStorage.setItem("first_name", perfil.first_name);
      if (perfil?.last_name) localStorage.setItem("last_name", perfil.last_name);
    } else {
      localStorage.setItem("theme", "dark");
      const condominioId = uc?.condominio?.id;
      if (condominioId) {
        localStorage.setItem("condominioId", String(condominioId));
        // El usuario puede tener otros condominios además del principal
        // (lista que empieza por el principal): arranca en el principal y
        // cambia con el selector del header, igual que el contador.
        const condominios = uc?.condominios_contador || [];
        if (condominios.length > 1) {
          localStorage.setItem("condominios_contador", JSON.stringify(condominios));
          localStorage.setItem("active_condominio_id", String(condominioId));
        }
        setMultimonedaFlag(!!(uc?.condominio?.multimoneda_activa));
        setTenantTipo(uc?.condominio?.tipo || "condominio");
      } else if (perfil?.is_superuser) {
        // Superadmin sin condominio propio: cargar todos los condominios para el
        // selector global, de modo que pueda navegar y probar el sistema real.
        try {
          const res    = await apiFetch("/api/core/admin/condominios/");
          const data   = res.ok ? await res.json() : [];
          const list   = Array.isArray(data) ? data : [];
          localStorage.setItem("condominios_admin", JSON.stringify(list));
          const validIds = list.map(c => String(c.id));
          const savedId  = localStorage.getItem("active_condominio_id");
          const activeId = savedId && validIds.includes(savedId)
            ? savedId
            : String(list[0]?.id ?? "");
          if (activeId) localStorage.setItem("active_condominio_id", activeId);
          const active = list.find(c => String(c.id) === String(activeId));
          setMultimonedaFlag(!!active?.multimoneda_activa);
          setTenantTipo(active?.tipo || "condominio");
        } catch { /* el panel admin sigue funcionando sin selector */ }
      } else {
        setMultimonedaFlag(!!(uc?.condominio?.multimoneda_activa));
        setTenantTipo(uc?.condominio?.tipo || "condominio");
      }
    }

    // Con el condominio activo ya elegido, los permisos que valen son los de
    // ese condominio (el admin puede personalizarlos por condominio).
    if (rol !== "RESIDENTE" && localStorage.getItem("active_condominio_id")) {
      try {
        const p2 = await getProfile();
        const permisos = p2?.usuario_condominio?.permisos;
        if (Array.isArray(permisos)) localStorage.setItem("permisos", JSON.stringify(permisos));
      } catch { /* quedan los generales; Layout los vuelve a pedir */ }
    }

    return data;
  }

  function logout() {
    limpiarSesion();
    // replace() avoids adding /login to the browser history stack so the user
    // can't navigate "back" into a protected page after logging out.
    // The timestamp query param forces the browser to discard any cached copy
    // of the page, ensuring a full asset reload.
    window.location.replace("/login?_=" + Date.now());
  }

  function isAuthenticated() {
    const token = localStorage.getItem("access_token");
    const refresh = localStorage.getItem("refresh_token");
    return Boolean(token && refresh);
  }

  return { login, logout, isAuthenticated };
}
